import { motion } from "framer-motion";
import { Factory, Gauge, Clock } from "lucide-react";
import { AnimatedNumber } from "./AnimatedNumber";
import { StatusPill } from "./StatusPill";
import { useLang } from "../../lib/i18n";

const fmtTime = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-IN", { hour: "2-digit", minute: "2-digit", day: "numeric", month: "short" }); } catch { return "—"; }
};

const Bars = ({ history }) => {
  const { lang } = useLang();
  const max = Math.max(1, ...history.map((h) => h?.output_t ?? 0));
  return (
    <div data-testid="production-history" className="flex items-end gap-2 sm:gap-3 h-28">
      {history.map((h, i) => (
        <div key={h.date} className="flex-1 h-full flex flex-col items-center gap-2">
          <div className="flex-1 w-full flex items-end">
          <motion.div
            initial={{ scaleY: 0 }}
            whileInView={{ scaleY: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1, delay: i * 0.07, ease: [0.22, 1, 0.36, 1] }}
            style={{ height: `${((h.output_t ?? 0) / max) * 100}%`, transformOrigin: "bottom" }}
            className={`w-full rounded-md ${i === history.length - 1 ? "bg-[var(--husk)]" : "bg-[#0F8A78]/70"}`}
          />
          </div>
          <span className="font-mono text-[11px] text-[var(--sage)]">{new Date(h.date).toLocaleDateString(lang === "hi" ? "hi-IN" : "en-IN", { weekday: "narrow" })}</span>
        </div>
      ))}
    </div>
  );
};

export const ProductionCard = ({ plant, history }) => {
  const { t } = useLang();
  const util = plant ? Math.min(100, plant.week_utilization ?? 0) : 0;
  return (
    <div data-testid="live-production-counter" className="relative h-full overflow-hidden rounded-[28px] border hairline bg-[var(--forest-2)] p-6 sm:p-10 flex flex-col">
      <div className="absolute -right-20 -top-20 h-72 w-72 rounded-full bg-[#0F8A78]/20 blur-3xl pointer-events-none" />
      <div className="relative flex flex-wrap items-center justify-between gap-4">
        <p className="eyebrow">{t("prod.today")}</p>
        <StatusPill status={plant?.status} testId="board-status-badge" />
      </div>
      <div className="relative mt-8 flex items-end gap-4">
        <AnimatedNumber value={plant?.today_output_t} testId="today-output-tonnes" className="font-display font-bold tracking-[-0.05em] leading-none text-7xl sm:text-[9rem]" />
        <span className="pb-3 sm:pb-6 font-serif-i text-3xl sm:text-4xl text-[var(--husk)]">{t("prod.tonnes")}</span>
      </div>
      <div className="relative mt-8">
        <div className="flex justify-between font-mono text-xs text-[var(--sage)] mb-2">
          <span>{t("prod.capacity")}</span>
          <span data-testid="capacity-utilization-value">{t("prod.capOf", { n: util, c: plant?.capacity_tpd ?? 0 })}</span>
        </div>
        <div data-testid="capacity-utilization-bar" className="h-2.5 rounded-full bg-white/5 overflow-hidden">
          <motion.div className="h-full rounded-full bg-gradient-to-r from-[var(--teal)] to-[var(--mint)]" initial={{ width: 0 }} whileInView={{ width: `${util}%` }} viewport={{ once: true }} transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }} />
        </div>
      </div>
      <div className="relative mt-8 grid grid-cols-3 gap-3 text-sm">
        <Stat icon={Factory} label={t("prod.presses")} value={`${plant?.presses_active ?? 0}/${plant?.presses_total ?? 0}`} testId="presses-active" />
        <Stat icon={Gauge} label={t("prod.daily")} value={`${plant?.capacity_tpd ?? 0} t`} testId="daily-capacity" />
        <Stat icon={Clock} label={t("prod.updated")} value={fmtTime(plant?.updated_at)} testId="last-updated" />
      </div>
      {plant?.notice && <p data-testid="plant-notice" className="relative mt-6 rounded-xl bg-[#E8B04B]/10 border border-[#E8B04B]/30 px-4 py-3 text-sm text-[var(--husk)]">{plant.notice}</p>}
      <div className="relative mt-auto pt-10">
        <p className="eyebrow mb-4">{t("prod.week")}</p>
        <Bars history={history} />
      </div>
    </div>
  );
};

const Stat = ({ icon: Icon, label, value, testId }) => (
  <div className="rounded-2xl border hairline bg-black/20 p-3 sm:p-4">
    <Icon className="h-4 w-4 text-[var(--mint)]" />
    <p className="mt-3 font-mono text-[11px] uppercase tracking-wider text-[var(--sage)]">{label}</p>
    <p data-testid={testId} className="mt-1 font-semibold text-[var(--cream)] text-sm sm:text-base">{value}</p>
  </div>
);
